// src/modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionPaymentPage.tsx

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../../redux/useDispatch";
import { RootState } from "../../../../../redux/store";
import routes from "../../../../../routes/routes";
import { parseSubscriptionQuery } from "../../common/url/subscriptionQuery";
import StripePayment from "../../../common/components/PaymentStep/StripePayment";
import LoaderOverlay from "../../../../layout/components/LoaderOverlay";
import InitializationErrorInfo from "../../../common/components/PaymentStep/InitializationErrorInfo";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import {
    initializePaymentProvider,
    paymentInitializationRetryStarted,
    bookingCreationCompleted,
    paymentCompleted,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";

/**
 * SubscriptionPaymentPage Component
 * ---------------------------------------------------------------
 * Handles the payment step for new subscription creation.
 *
 * @remarks
 * This page:
 * - Initializes Stripe payment provider for subscription
 * - Displays Stripe checkout UI
 * - Redirects to orders page after successful payment
 * - Handles payment initialization errors with retry option
 * - Guards against missing prerequisites (selectedPlan, hostData)
 *
 * @example
 * Route: /key-handover/subscription/payment?locationId=loc-123&plan=monthly&...
 */
const SubscriptionPaymentPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const selectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );
    const hostData = useSelector((state: RootState) => state.subscription.newSubscription.hostData);
    const paymentProvider = useSelector(
        (state: RootState) => state.bookingCreation.payment.provider
    );
    const paymentInitFailed = useSelector(
        (state: RootState) => state.bookingCreation.payment.paymentInitFailed
    );

    const stripePayment = useSelector((state: RootState) => state.bookingCreation.payment.stripe);
    const orderIds = useSelector((state: RootState) => state.bookingCreation.payment.orderIds);

    const [isLoading, setIsLoading] = useState(true);

    // Guard: require selectedPlan and hostData
    useEffect(() => {
        const subscriptionQuery = parseSubscriptionQuery(location.search);

        if (!subscriptionQuery.locationId) {
            navigate(
                { pathname: routes.keyHandoverLocationSelectionPath, search: location.search },
                { replace: true }
            );
            return;
        }

        if (!selectedPlan) {
            navigate(
                {
                    pathname: routes.keyHandoverSubscriptionPlanSelectionPath,
                    search: location.search,
                },
                { replace: true }
            );
            return;
        }

        if (!hostData || !hostData.firstName || !hostData.lastName || !hostData.email) {
            navigate(
                { pathname: routes.keyHandoverSubscriptionHostDataPath, search: location.search },
                { replace: true }
            );
            return;
        }
    }, [location.search, navigate, selectedPlan, hostData]);

    // Redirect to success page after successful payment
    useEffect(() => {
        if (orderIds?.length) {
            navigate({
                pathname: routes.keyHandoverSubscriptionSuccessPath,
                search: location.search,
            });
            setTimeout(() => dispatch(bookingCreationCompleted()), 1000);
        }
    }, [orderIds, dispatch, navigate, location.search]);

    // Initialize Stripe payment provider (subscriptions always require payment)
    useEffect(() => {
        dispatch(initializePaymentProvider("stripe"));
        setIsLoading(false);
    }, [dispatch]);

    // Loading state
    if (isLoading || !paymentProvider) {
        return <LoaderOverlay open={true} />;
    }

    // Error state with retry - navigates back to summary page
    if (paymentInitFailed) {
        const showTryAgain = Boolean(stripePayment.token && stripePayment?.checkoutFrontendUrl);

        return (
            <KeyHandoverPageLayout
                headerText={t("book_location.keys.subscription_payment.header_text", {
                    defaultValue: "Payment",
                })}
                backTo={routes.keyHandoverSubscriptionSummaryPath}
            >
                <KeyHandoverContentWrapper>
                    <InitializationErrorInfo
                        showTryAgain={showTryAgain}
                        onBackClick={() => {
                            navigate({
                                pathname: routes.keyHandoverSubscriptionSummaryPath,
                                search: location.search,
                            });
                        }}
                        onTryAgainClick={() => dispatch(paymentInitializationRetryStarted({}))}
                    />
                </KeyHandoverContentWrapper>
            </KeyHandoverPageLayout>
        );
    }

    // Render Stripe payment UI
    if (paymentProvider === "stripe") {
        return (
            <>
                <StripePayment service="subscription" />
            </>
        );
    }

    return <LoaderOverlay open={true} />;
};

export default SubscriptionPaymentPage;
